import React, { memo, ReactNode } from 'react';
import styled from '@emotion/styled';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { alpha } from '@mui/material/styles';
import Constants from 'style/constants';
import { useVirtualization } from 'shared/hooks/virtualization';

import styles from './virtualized-tree-item.module.scss';

interface VirtualizedTreeItemProps {
  itemId: string;
  label: ReactNode;
  onClick?: () => void;
  isSelectable?: boolean;
  level: number;
  hasChildren?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  containerSelector?: string;
}

const TreeItemContainer = styled.div<{ $isSelectable?: boolean }>(({ $isSelectable = true }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: '20px',
  margin: '0px 4px',
  padding: '0px 12px',
  height: '100%',
  borderRadius: '28px',
  width: '100%',
  cursor: $isSelectable ? 'pointer' : 'default',
  opacity: $isSelectable ? 1 : 0.7,
  position: 'relative',
  '&:hover': {
    backgroundColor: $isSelectable ? alpha(Constants.Colors.secondaryContainer, 0.16) : 'transparent',
  },
  '&.selected': {
    backgroundColor: $isSelectable ? Constants.Colors.secondaryContainer : 'transparent',
    borderRadius: $isSelectable ? '28px' : '0px',
  },
}));

const VirtualizedTreeItem = memo<VirtualizedTreeItemProps>(
  ({ label, onClick, isSelectable = true, level, hasChildren = false, isExpanded = false, onToggle, containerSelector }) => {
    const [ref, isVisible] = useVirtualization<HTMLDivElement>(containerSelector);

    if (!isVisible) {
      return <div ref={ref} className={styles.virtualizedTreeItemPlaceholder} />;
    }

    const handleIconClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasChildren && onToggle) {
        onToggle();
      }
    };

    const handleItemClick = () => {
      if (isSelectable && onClick) {
        onClick();
      }
    };

    return (
      <div ref={ref}>
        <div className={styles.treeItemWrapper}>
          <div className={styles.levelDividers}>
            {Array.from({ length: level }, (_, index) => (
              <div key={index} className={styles.levelDivider} />
            ))}
          </div>
          <TreeItemContainer $isSelectable={isSelectable} onClick={handleItemClick}>
            {hasChildren && (
              <button className={styles.virtualizedTreeItemExpandIcon} onClick={handleIconClick}>
                {isExpanded ? <ArrowDropDownIcon fontSize="small" /> : <ArrowRightIcon fontSize="small" />}
              </button>
            )}
            {label}
          </TreeItemContainer>
        </div>
      </div>
    );
  },
);

export default VirtualizedTreeItem;
